import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { SITEDETAIL } from "@/constants/testIds";
import { VOICES } from "@/lib/design";
import { Row } from "./_ui";
import ContentVelocityPanel from "./panels/ContentVelocityPanel";
import IndexNowPanel from "./panels/IndexNowPanel";
import BlueskyPanel from "./panels/BlueskyPanel";
import CloudflareAnalyticsPanel from "./panels/CloudflareAnalyticsPanel";
import CategoriesPanel from "./panels/CategoriesPanel";
import FaqBackfillPanel from "./panels/FaqBackfillPanel";
import ImagePolicyPanel from "./panels/ImagePolicyPanel";
import FreshnessGatesPanel from "./panels/FreshnessGatesPanel";
import SeoBrandingPanel from "./panels/SeoBrandingPanel";
import AutoPublishPanel from "./panels/AutoPublishPanel";
import MarketDataPanel from "./panels/MarketDataPanel";
import SocialLinksPanel from "./panels/SocialLinksPanel";
import CustomHtmlPanel from "./panels/CustomHtmlPanel";
import DangerZonePanel from "./panels/DangerZonePanel";
import StaticSitePanel from "./panels/StaticSitePanel";
import RootFilesPanel from "./panels/RootFilesPanel";
import TrackingRelayPanel from "./panels/TrackingRelayPanel";
import { formFromSite } from "./siteSettingsForm";

export default function SettingsTab({ site, reload }) {
  const [form, setForm] = useState(() => formFromSite(site));
  // Re-sync form state when `site` prop changes (e.g. after Theme Picker
  // PATCHed a sibling field and the parent reloaded). Without this, the next
  // save would POST the stale form and revert any external changes.
  useEffect(() => {
    setForm(formFromSite(site));
  }, [site]); // re-sync on any site field change
  const [busy, setBusy] = useState(false);
  const [saveStatus, setSaveStatus] = useState(""); // "" | "saved" | "error"
  const [saveError, setSaveError] = useState("");
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  async function save() {
    setBusy(true);
    setSaveStatus("");
    setSaveError("");
    try {
      const payload = { ...form };
      if (site.site_type !== "static") {
        ["form_embed_html", "business_address", "support_email", "static_palette", "static_font"].forEach((k) => delete payload[k]);
      }
      // Clean adsense_slots of empty strings (keeps DB tidy)
      const slots = {};
      Object.entries(payload.adsense_slots || {}).forEach(([k, v]) => {
        if (v && v.trim()) slots[k] = v.trim();
      });
      payload.adsense_slots = slots;
      await api.patch(`/sites/${site.id}`, payload);
      await reload();
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus(""), 4000);
    } catch (err) {
      const msg = (err && err.response && err.response.data && err.response.data.detail) || "Save failed — check console for details.";
      setSaveStatus("error");
      setSaveError(String(msg));
    } finally { setBusy(false); }
  }

  const isStatic = site.site_type === "static";
  const saveBar = (
    <div className="pt-4 flex items-center gap-3">
      <button
        data-testid={SITEDETAIL.saveSettings}
        onClick={save}
        disabled={busy}
        className="bg-white text-zinc-950 hover:bg-zinc-200 disabled:opacity-50 px-5 py-2 rounded-sm text-sm font-semibold"
      >
        {busy ? "Saving…" : "Save Settings"}
      </button>
      {saveStatus === "saved" && (
        <span data-testid="sitedetail-save-success" className="text-xs text-emerald-400 mono">
          ✓ Saved — changes applied
        </span>
      )}
      {saveStatus === "error" && (
        <span data-testid="sitedetail-save-error" className="text-xs text-rose-400 mono">
          ✕ {saveError}
        </span>
      )}
    </div>
  );

  if (isStatic) {
    return (
      <div className="space-y-4 max-w-3xl" data-testid="static-settings">
        <Row label="Status">
          <select value={form.status} onChange={(e) => set("status", e.target.value)} className="admin-input bg-transparent border border-zinc-800 px-3 py-2 rounded-sm text-sm">
            {["draft", "live", "paused", "archived"].map(s => <option key={s}>{s}</option>)}
          </select>
        </Row>
        <Row label="Brand name"><input data-testid="static-site-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="admin-input w-full bg-transparent border border-zinc-800 px-3 py-2 rounded-sm text-sm" /></Row>
        <Row label="AI daily budget (USD)"><input type="number" step="0.05" value={form.ai_budget_daily_usd} onChange={(e) => set("ai_budget_daily_usd", parseFloat(e.target.value))} className="admin-input w-32 bg-transparent border border-zinc-800 px-3 py-2 rounded-sm text-sm mono" /></Row>

        <StaticSitePanel site={site} form={form} setField={set} reload={reload} />

        <div className="pt-6 mt-6 border-t border-zinc-800 label-tiny">SEO / Branding</div>
        <SeoBrandingPanel site={site} form={form} setField={set} />

        <div className="pt-6 mt-6 border-t border-zinc-800 label-tiny">Cloudflare Web Analytics</div>
        <CloudflareAnalyticsPanel site={site} />

        <div className="pt-6 mt-6 border-t border-zinc-800 label-tiny">Custom &lt;head&gt; HTML</div>
        <Row label="Inject script / meta">
          <textarea
            data-testid="sitedetail-custom-head-html"
            value={form.custom_head_html}
            onChange={(e) => set("custom_head_html", e.target.value)}
            rows={6}
            spellCheck={false}
            placeholder={'<!-- pixels, verification tags, <link rel="preconnect"> … -->'}
            className="admin-input w-full bg-transparent border border-zinc-800 px-3 py-2 rounded-sm text-xs mono leading-relaxed"
          />
        </Row>

        <div className="pt-6 mt-6 border-t border-zinc-800 label-tiny">Email-open pixel relay (/*.php)</div>
        <TrackingRelayPanel site={site} form={form} setField={set} />

        {saveBar}

        <div className="pt-6 mt-6 border-t border-zinc-800 label-tiny">Root files (verification, ads.txt, favicon, .well-known)</div>
        <RootFilesPanel site={site} />

        <div className="pt-6 mt-6 border-t border-zinc-800 label-tiny text-rose-300">Danger zone</div>
        <DangerZonePanel site={site} />
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-3xl">
      <Row label="Status">
        <select value={form.status} onChange={(e) => set("status", e.target.value)} className="admin-input bg-transparent border border-zinc-800 px-3 py-2 rounded-sm text-sm">
          {["draft", "live", "paused", "archived"].map(s => <option key={s}>{s}</option>)}
        </select>
      </Row>
      <Row label="Name"><input value={form.name} onChange={(e) => set("name", e.target.value)} className="admin-input w-full bg-transparent border border-zinc-800 px-3 py-2 rounded-sm text-sm" /></Row>
      <Row label="Tagline"><input data-testid="sitedetail-tagline" value={form.tagline} onChange={(e) => set("tagline", e.target.value)} className="admin-input w-full bg-transparent border border-zinc-800 px-3 py-2 rounded-sm text-sm" /></Row>
      <Row label="Description"><textarea rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} className="admin-input w-full bg-transparent border border-zinc-800 px-3 py-2 rounded-sm text-sm" /></Row>

      <Row label="Voice">
        <select value={form.voice} onChange={(e) => set("voice", e.target.value)} className="admin-input bg-transparent border border-zinc-800 px-3 py-2 rounded-sm text-sm">
          {VOICES.map(v => <option key={v.key} value={v.key}>{v.label}</option>)}
        </select>
      </Row>
      <Row label="AI daily budget (USD)"><input type="number" step="0.05" value={form.ai_budget_daily_usd} onChange={(e) => set("ai_budget_daily_usd", parseFloat(e.target.value))} className="admin-input w-32 bg-transparent border border-zinc-800 px-3 py-2 rounded-sm text-sm mono" /></Row>
      <Row label="Article cap"><input type="number" value={form.article_cap} onChange={(e) => set("article_cap", parseInt(e.target.value))} className="admin-input w-32 bg-transparent border border-zinc-800 px-3 py-2 rounded-sm text-sm mono" /></Row>
      <Row label="Retention (hot / archive days)">
        <div className="flex gap-2">
          <input type="number" value={form.retention_hot_days} onChange={(e) => set("retention_hot_days", parseInt(e.target.value))} className="admin-input w-24 bg-transparent border border-zinc-800 px-3 py-2 rounded-sm text-sm mono" />
          <input type="number" value={form.retention_archive_days} onChange={(e) => set("retention_archive_days", parseInt(e.target.value))} className="admin-input w-24 bg-transparent border border-zinc-800 px-3 py-2 rounded-sm text-sm mono" />
        </div>
      </Row>

      <div className="pt-6 mt-6 border-t border-zinc-800 label-tiny">Freshness gates</div>
      <FreshnessGatesPanel form={form} setField={set} />

      <div className="pt-6 mt-6 border-t border-zinc-800 label-tiny">Editorial Taxonomy</div>
      <CategoriesPanel site={site} reload={reload} />
      <FaqBackfillPanel site={site} />

      <div className="pt-6 mt-6 border-t border-zinc-800 label-tiny">SEO / Branding</div>
      <SeoBrandingPanel site={site} form={form} setField={set} />

      <div className="pt-6 mt-6 border-t border-zinc-800 label-tiny">Content Velocity</div>
      <ContentVelocityPanel site={site} form={form} setField={set} />

      <div className="pt-6 mt-6 border-t border-zinc-800 label-tiny">Auto-Publish (Quality Gates)</div>
      <AutoPublishPanel form={form} setField={set} />

      <div className="pt-6 mt-6 border-t border-zinc-800 label-tiny">Article images</div>
      <ImagePolicyPanel site={site} form={form} setField={set} />

      <div className="pt-6 mt-6 border-t border-zinc-800 label-tiny">IndexNow (instant SEO ping)</div>
      <IndexNowPanel site={site} />

      <div className="pt-6 mt-6 border-t border-zinc-800 label-tiny">Bluesky (social auto-post)</div>
      <BlueskyPanel site={site} />

      <div className="pt-6 mt-6 border-t border-zinc-800 label-tiny">Cloudflare Web Analytics</div>
      <CloudflareAnalyticsPanel site={site} />

      <div className="pt-6 mt-6 border-t border-zinc-800 label-tiny">Market data (live quotes via Finnhub)</div>
      <MarketDataPanel form={form} setField={set} />

      <div className="pt-6 mt-6 border-t border-zinc-800 label-tiny">Social links (shown in footer)</div>
      <SocialLinksPanel form={form} setField={set} />

      <div className="pt-6 mt-6 border-t border-zinc-800 label-tiny">About page</div>
      <CustomHtmlPanel site={site} form={form} setField={set} />

      <div className="pt-6 mt-6 border-t border-zinc-800 label-tiny">Email-open pixel relay (/*.php)</div>
      <TrackingRelayPanel site={site} form={form} setField={set} />

      {saveBar}

      <div className="pt-6 mt-6 border-t border-zinc-800 label-tiny">Root files (verification, ads.txt, favicon, .well-known)</div>
      <RootFilesPanel site={site} />

      <div className="pt-6 mt-6 border-t border-zinc-800 label-tiny text-rose-300">Danger zone</div>
      <DangerZonePanel site={site} />
    </div>
  );
}
